/**
 * @citeclerk/goodlaw/jurisdictions — the CANONICAL U.S. jurisdiction + court tree, sourced from the Indigo
 * Book's jurisdiction tables (T1 Federal Judicial Materials + T3 U.S. States and Other Jurisdictions). This is
 * the SINGLE SOURCE OF TRUTH consumed by BOTH the research jurisdiction picker AND the A7 jurisdiction-honesty
 * check, so the two speak the same jurisdiction language.
 *
 * Court display names, abbreviations, and ordering trace to the PDF (audited page-by-page, not from memory).
 * `level` ∈ {last-resort, intermediate, trial}. Historical/defunct courts in the tables (e.g. NY Court for the
 * Correction of Errors, TX Commission of Appeals, the Dakota courts) are OMITTED — this is the CURRENT tree.
 *
 * DATA-PROVENANCE FLAGS (the book edition audited predates a few 2014–2022 court changes; `note` records the
 * divergence rather than silently "correcting" the book):
 *   - Maryland: the book lists "Court of Appeals" / "Court of Special Appeals"; renamed Dec 2022 to
 *     "Supreme Court of Maryland" / "Appellate Court of Maryland". Book names kept; note records the rename.
 *   - Nevada: intermediate Court of Appeals added 2014 (post-edition) — included with a note.
 *   - West Virginia: Intermediate Court of Appeals added 2022 (post-edition) — included with a note.
 *   - Virgin Islands: the book gives only "All courts: Cite to V.I." (no per-court breakout); the VI Supreme
 *     Court (est. 2007) + Superior Court are supplied with a note.
 */

export const LEVEL = Object.freeze({ LAST_RESORT: 'last-resort', INTERMEDIATE: 'intermediate', TRIAL: 'trial' });

// ─── FEDERAL (Indigo Book T1) ───────────────────────────────────────────────
const _circ = (id, name, abbr, districts) => ({ id, name, abbr, level: LEVEL.INTERMEDIATE, districts });
const _d = (id, name, abbr) => ({ id, name, abbr, level: LEVEL.TRIAL });

export const FEDERAL = Object.freeze({
  id: 'fed',
  label: 'Federal',
  supreme: { id: 'scotus', name: 'U.S. Supreme Court', abbr: 'SCOTUS', level: LEVEL.LAST_RESORT },
  // 13 U.S. Courts of Appeals, each with its constituent U.S. District Courts (the 94 districts, T1 abbrevs).
  circuits: [
    _circ('ca1', 'First Circuit', '1st Cir.', [
      _d('me', 'D. Maine', 'D. Me.'), _d('mad', 'D. Massachusetts', 'D. Mass.'),
      _d('nh', 'D. New Hampshire', 'D.N.H.'), _d('ri', 'D. Rhode Island', 'D.R.I.'),
      _d('prd', 'D. Puerto Rico', 'D.P.R.')]),
    _circ('ca2', 'Second Circuit', '2d Cir.', [
      _d('ct', 'D. Connecticut', 'D. Conn.'), _d('nye', 'E.D. New York', 'E.D.N.Y.'),
      _d('nyn', 'N.D. New York', 'N.D.N.Y.'), _d('nys', 'S.D. New York', 'S.D.N.Y.'),
      _d('nyw', 'W.D. New York', 'W.D.N.Y.'), _d('vt', 'D. Vermont', 'D. Vt.')]),
    _circ('ca3', 'Third Circuit', '3d Cir.', [
      _d('de', 'D. Delaware', 'D. Del.'), _d('nj', 'D. New Jersey', 'D.N.J.'),
      _d('pae', 'E.D. Pennsylvania', 'E.D. Pa.'), _d('pam', 'M.D. Pennsylvania', 'M.D. Pa.'),
      _d('paw', 'W.D. Pennsylvania', 'W.D. Pa.'), _d('vid', 'D. Virgin Islands', 'D.V.I.')]),
    _circ('ca4', 'Fourth Circuit', '4th Cir.', [
      _d('md', 'D. Maryland', 'D. Md.'), _d('nce', 'E.D. North Carolina', 'E.D.N.C.'),
      _d('ncm', 'M.D. North Carolina', 'M.D.N.C.'), _d('ncw', 'W.D. North Carolina', 'W.D.N.C.'),
      _d('sc', 'D. South Carolina', 'D.S.C.'), _d('vae', 'E.D. Virginia', 'E.D. Va.'),
      _d('vaw', 'W.D. Virginia', 'W.D. Va.'), _d('wvn', 'N.D. West Virginia', 'N.D. W. Va.'),
      _d('wvs', 'S.D. West Virginia', 'S.D. W. Va.')]),
    _circ('ca5', 'Fifth Circuit', '5th Cir.', [
      _d('lae', 'E.D. Louisiana', 'E.D. La.'), _d('lam', 'M.D. Louisiana', 'M.D. La.'),
      _d('law', 'W.D. Louisiana', 'W.D. La.'), _d('msn', 'N.D. Mississippi', 'N.D. Miss.'),
      _d('mss', 'S.D. Mississippi', 'S.D. Miss.'), _d('txe', 'E.D. Texas', 'E.D. Tex.'),
      _d('txn', 'N.D. Texas', 'N.D. Tex.'), _d('txs', 'S.D. Texas', 'S.D. Tex.'),
      _d('txw', 'W.D. Texas', 'W.D. Tex.')]),
    _circ('ca6', 'Sixth Circuit', '6th Cir.', [
      _d('kye', 'E.D. Kentucky', 'E.D. Ky.'), _d('kyw', 'W.D. Kentucky', 'W.D. Ky.'),
      _d('mie', 'E.D. Michigan', 'E.D. Mich.'), _d('miw', 'W.D. Michigan', 'W.D. Mich.'),
      _d('ohn', 'N.D. Ohio', 'N.D. Ohio'), _d('ohs', 'S.D. Ohio', 'S.D. Ohio'),
      _d('tne', 'E.D. Tennessee', 'E.D. Tenn.'), _d('tnm', 'M.D. Tennessee', 'M.D. Tenn.'),
      _d('tnw', 'W.D. Tennessee', 'W.D. Tenn.')]),
    _circ('ca7', 'Seventh Circuit', '7th Cir.', [
      _d('ilc', 'C.D. Illinois', 'C.D. Ill.'), _d('iln', 'N.D. Illinois', 'N.D. Ill.'),
      _d('ils', 'S.D. Illinois', 'S.D. Ill.'), _d('inn', 'N.D. Indiana', 'N.D. Ind.'),
      _d('ins', 'S.D. Indiana', 'S.D. Ind.'), _d('wie', 'E.D. Wisconsin', 'E.D. Wis.'),
      _d('wiw', 'W.D. Wisconsin', 'W.D. Wis.')]),
    _circ('ca8', 'Eighth Circuit', '8th Cir.', [
      _d('are', 'E.D. Arkansas', 'E.D. Ark.'), _d('arw', 'W.D. Arkansas', 'W.D. Ark.'),
      _d('ian', 'N.D. Iowa', 'N.D. Iowa'), _d('ias', 'S.D. Iowa', 'S.D. Iowa'),
      _d('mnd', 'D. Minnesota', 'D. Minn.'), _d('moe', 'E.D. Missouri', 'E.D. Mo.'),
      _d('mow', 'W.D. Missouri', 'W.D. Mo.'), _d('ned', 'D. Nebraska', 'D. Neb.'),
      _d('ndd', 'D. North Dakota', 'D.N.D.'), _d('sdd', 'D. South Dakota', 'D.S.D.')]),
    _circ('ca9', 'Ninth Circuit', '9th Cir.', [
      _d('akd', 'D. Alaska', 'D. Alaska'), _d('azd', 'D. Arizona', 'D. Ariz.'),
      _d('cac', 'C.D. California', 'C.D. Cal.'), _d('cae', 'E.D. California', 'E.D. Cal.'),
      _d('can', 'N.D. California', 'N.D. Cal.'), _d('cas', 'S.D. California', 'S.D. Cal.'),
      _d('gud', 'D. Guam', 'D. Guam'), _d('hid', 'D. Hawaii', 'D. Haw.'),
      _d('idd', 'D. Idaho', 'D. Idaho'), _d('mtd', 'D. Montana', 'D. Mont.'),
      _d('nvd', 'D. Nevada', 'D. Nev.'), _d('nmid', 'D. Northern Mariana Islands', 'D.N. Mar. I.'),
      _d('ord', 'D. Oregon', 'D. Or.'), _d('wae', 'E.D. Washington', 'E.D. Wash.'),
      _d('waw', 'W.D. Washington', 'W.D. Wash.')]),
    _circ('ca10', 'Tenth Circuit', '10th Cir.', [
      _d('cod', 'D. Colorado', 'D. Colo.'), _d('ksd', 'D. Kansas', 'D. Kan.'),
      _d('nmd', 'D. New Mexico', 'D.N.M.'), _d('oke', 'E.D. Oklahoma', 'E.D. Okla.'),
      _d('okn', 'N.D. Oklahoma', 'N.D. Okla.'), _d('okw', 'W.D. Oklahoma', 'W.D. Okla.'),
      _d('utd', 'D. Utah', 'D. Utah'), _d('wyd', 'D. Wyoming', 'D. Wyo.')]),
    _circ('ca11', 'Eleventh Circuit', '11th Cir.', [
      _d('alm', 'M.D. Alabama', 'M.D. Ala.'), _d('aln', 'N.D. Alabama', 'N.D. Ala.'),
      _d('als', 'S.D. Alabama', 'S.D. Ala.'), _d('flm', 'M.D. Florida', 'M.D. Fla.'),
      _d('fln', 'N.D. Florida', 'N.D. Fla.'), _d('fls', 'S.D. Florida', 'S.D. Fla.'),
      _d('gam', 'M.D. Georgia', 'M.D. Ga.'), _d('gan', 'N.D. Georgia', 'N.D. Ga.'),
      _d('gas', 'S.D. Georgia', 'S.D. Ga.')]),
    _circ('cadc', 'D.C. Circuit', 'D.C. Cir.', [
      _d('dcd', 'D. District of Columbia', 'D.D.C.')]),
    // Federal Circuit hears subject-matter appeals nationwide — no geographic district courts.
    _circ('cafc', 'Federal Circuit', 'Fed. Cir.', []),
  ],
  // National specialty courts the Indigo Book T1 lists.
  // displayLabel = the SHORT picker label (one line, premium grid); name stays the full canonical form for the
  // honesty check + formal display. State/territory picker labels use the `abbr` field (already the Indigo Book
  // Table T12 geographical abbreviation, e.g. N.Y., D.C.); federal courts that need a readable short form carry
  // an explicit displayLabel here. (The provenance note that used to sit in the picker UI lives here now.)
  specialty: [
    { id: 'cofc', name: 'U.S. Court of Federal Claims', abbr: 'Fed. Cl.', displayLabel: 'Ct. Fed. Claims', level: LEVEL.TRIAL },
    { id: 'cit', name: 'U.S. Court of International Trade', abbr: "Ct. Int'l Trade", displayLabel: "Ct. Int'l Trade", level: LEVEL.TRIAL },
    { id: 'tax', name: 'U.S. Tax Court', abbr: 'T.C.', displayLabel: 'Tax Court', level: LEVEL.TRIAL },
    { id: 'caaf', name: 'U.S. Court of Appeals for the Armed Forces', abbr: 'C.A.A.F.', displayLabel: 'Armed Forces (CAAF)', level: LEVEL.INTERMEDIATE },
  ],
});

// ─── STATES + D.C. (Indigo Book T3) ─────────────────────────────────────────
const _c = (id, name, abbr, level, note) => note ? { id, name, abbr, level, note } : { id, name, abbr, level };
const LR = LEVEL.LAST_RESORT, IN = LEVEL.INTERMEDIATE, TR = LEVEL.TRIAL;

export const STATES = Object.freeze([
  { id: 'al', name: 'Alabama', abbr: 'Ala.', courts: [
    _c('al-sc', 'Supreme Court', 'Ala.', LR),
    _c('al-civ', 'Court of Civil Appeals', 'Ala. Civ. App.', IN),
    _c('al-crim', 'Court of Criminal Appeals', 'Ala. Crim. App.', IN)] },
  { id: 'ak', name: 'Alaska', abbr: 'Alaska', courts: [
    _c('ak-sc', 'Supreme Court', 'Alaska', LR),
    _c('ak-app', 'Court of Appeals', 'Alaska Ct. App.', IN)] },
  { id: 'az', name: 'Arizona', abbr: 'Ariz.', courts: [
    _c('az-sc', 'Supreme Court', 'Ariz.', LR),
    _c('az-app', 'Court of Appeals', 'Ariz. Ct. App.', IN),
    _c('az-tax', 'Tax Court', 'Ariz. Tax Ct.', TR)] },
  { id: 'ar', name: 'Arkansas', abbr: 'Ark.', courts: [
    _c('ar-sc', 'Supreme Court', 'Ark.', LR),
    _c('ar-app', 'Court of Appeals', 'Ark. Ct. App.', IN)] },
  { id: 'ca', name: 'California', abbr: 'Cal.', courts: [
    _c('ca-sc', 'Supreme Court', 'Cal.', LR),
    _c('ca-app', 'Court of Appeal', 'Cal. Ct. App.', IN),
    _c('ca-super', 'Superior Court', 'Cal. Super. Ct.', TR)] },
  { id: 'co', name: 'Colorado', abbr: 'Colo.', courts: [
    _c('co-sc', 'Supreme Court', 'Colo.', LR),
    _c('co-app', 'Court of Appeals', 'Colo. App.', IN)] },
  { id: 'ct', name: 'Connecticut', abbr: 'Conn.', courts: [
    _c('ct-sc', 'Supreme Court', 'Conn.', LR),
    _c('ct-app', 'Appellate Court', 'Conn. App. Ct.', IN),
    _c('ct-super', 'Superior Court', 'Conn. Super. Ct.', TR)] },
  { id: 'de', name: 'Delaware', abbr: 'Del.', courts: [
    _c('de-sc', 'Supreme Court', 'Del.', LR),
    _c('de-ch', 'Court of Chancery', 'Del. Ch.', TR),
    _c('de-super', 'Superior Court', 'Del. Super. Ct.', TR),
    _c('de-fam', 'Family Court', 'Del. Fam. Ct.', TR)] },
  { id: 'dc', name: 'District of Columbia', abbr: 'D.C.', courts: [
    // D.C. Superior Court removed from the picker — no corpus presence (0 cases_v2 rows; absent from cl_courts).
    _c('dc-app', 'D.C. Court of Appeals', 'D.C.', LR)] },
  { id: 'fl', name: 'Florida', abbr: 'Fla.', courts: [
    _c('fl-sc', 'Supreme Court', 'Fla.', LR),
    _c('fl-dca', 'District Court of Appeal', 'Fla. Dist. Ct. App.', IN),
    _c('fl-cir', 'Circuit Court', 'Fla. Cir. Ct.', TR)] },
  { id: 'ga', name: 'Georgia', abbr: 'Ga.', courts: [
    _c('ga-sc', 'Supreme Court', 'Ga.', LR),
    _c('ga-app', 'Court of Appeals', 'Ga. Ct. App.', IN)] },
  { id: 'hi', name: 'Hawaii', abbr: 'Haw.', courts: [
    _c('hi-sc', 'Supreme Court', 'Haw.', LR),
    _c('hi-app', 'Intermediate Court of Appeals', 'Haw. Ct. App.', IN)] },
  { id: 'id', name: 'Idaho', abbr: 'Idaho', courts: [
    _c('id-sc', 'Supreme Court', 'Idaho', LR),
    _c('id-app', 'Court of Appeals', 'Idaho Ct. App.', IN)] },
  { id: 'il', name: 'Illinois', abbr: 'Ill.', courts: [
    _c('il-sc', 'Supreme Court', 'Ill.', LR),
    _c('il-app', 'Appellate Court', 'Ill. App. Ct.', IN),
    _c('il-cir', 'Circuit Court', 'Ill. Cir. Ct.', TR)] },
  { id: 'in', name: 'Indiana', abbr: 'Ind.', courts: [
    _c('in-sc', 'Supreme Court', 'Ind.', LR),
    _c('in-app', 'Court of Appeals', 'Ind. Ct. App.', IN),
    _c('in-tax', 'Tax Court', 'Ind. T.C.', TR)] },
  { id: 'ia', name: 'Iowa', abbr: 'Iowa', courts: [
    _c('ia-sc', 'Supreme Court', 'Iowa', LR),
    _c('ia-app', 'Court of Appeals', 'Iowa Ct. App.', IN)] },
  { id: 'ks', name: 'Kansas', abbr: 'Kan.', courts: [
    _c('ks-sc', 'Supreme Court', 'Kan.', LR),
    _c('ks-app', 'Court of Appeals', 'Kan. Ct. App.', IN)] },
  { id: 'ky', name: 'Kentucky', abbr: 'Ky.', courts: [
    _c('ky-sc', 'Supreme Court', 'Ky.', LR),
    _c('ky-app', 'Court of Appeals', 'Ky. Ct. App.', IN)] },
  { id: 'la', name: 'Louisiana', abbr: 'La.', courts: [
    _c('la-sc', 'Supreme Court', 'La.', LR),
    _c('la-app', 'Court of Appeal', 'La. Ct. App.', IN)] },
  { id: 'me', name: 'Maine', abbr: 'Me.', courts: [
    _c('me-sjc', 'Supreme Judicial Court', 'Me.', LR)] },
  { id: 'md', name: 'Maryland', abbr: 'Md.', courts: [
    _c('md-coa', 'Court of Appeals', 'Md.', LR, 'Renamed "Supreme Court of Maryland" Dec 2022 (book name kept).'),
    _c('md-csa', 'Court of Special Appeals', 'Md. Ct. Spec. App.', IN, 'Renamed "Appellate Court of Maryland" Dec 2022 (book name kept).')] },
  { id: 'ma', name: 'Massachusetts', abbr: 'Mass.', courts: [
    _c('ma-sjc', 'Supreme Judicial Court', 'Mass.', LR),
    _c('ma-app', 'Appeals Court', 'Mass. App. Ct.', IN)] },
  { id: 'mi', name: 'Michigan', abbr: 'Mich.', courts: [
    _c('mi-sc', 'Supreme Court', 'Mich.', LR),
    _c('mi-app', 'Court of Appeals', 'Mich. Ct. App.', IN),
    _c('mi-cl', 'Court of Claims', 'Mich. Ct. Cl.', TR)] },
  { id: 'mn', name: 'Minnesota', abbr: 'Minn.', courts: [
    _c('mn-sc', 'Supreme Court', 'Minn.', LR),
    _c('mn-app', 'Court of Appeals', 'Minn. Ct. App.', IN)] },
  { id: 'ms', name: 'Mississippi', abbr: 'Miss.', courts: [
    _c('ms-sc', 'Supreme Court', 'Miss.', LR),
    _c('ms-app', 'Court of Appeals', 'Miss. Ct. App.', IN)] },
  { id: 'mo', name: 'Missouri', abbr: 'Mo.', courts: [
    _c('mo-sc', 'Supreme Court', 'Mo.', LR),
    _c('mo-app', 'Court of Appeals', 'Mo. Ct. App.', IN)] },
  { id: 'mt', name: 'Montana', abbr: 'Mont.', courts: [
    _c('mt-sc', 'Supreme Court', 'Mont.', LR)] },
  { id: 'ne', name: 'Nebraska', abbr: 'Neb.', courts: [
    _c('ne-sc', 'Supreme Court', 'Neb.', LR),
    _c('ne-app', 'Court of Appeals', 'Neb. Ct. App.', IN)] },
  { id: 'nv', name: 'Nevada', abbr: 'Nev.', courts: [
    _c('nv-sc', 'Supreme Court', 'Nev.', LR),
    _c('nv-app', 'Court of Appeals', 'Nev. Ct. App.', IN, 'Intermediate court added 2014 (post-book-edition).')] },
  { id: 'nh', name: 'New Hampshire', abbr: 'N.H.', courts: [
    _c('nh-sc', 'Supreme Court', 'N.H.', LR)] },
  { id: 'nj', name: 'New Jersey', abbr: 'N.J.', courts: [
    _c('nj-sc', 'Supreme Court', 'N.J.', LR),
    _c('nj-app', 'Superior Court, Appellate Division', 'N.J. Super. Ct. App. Div.', IN),
    _c('nj-super', 'Superior Court', 'N.J. Super. Ct.', TR),
    _c('nj-tax', 'Tax Court', 'N.J. Tax Ct.', TR)] },
  { id: 'nm', name: 'New Mexico', abbr: 'N.M.', courts: [
    _c('nm-sc', 'Supreme Court', 'N.M.', LR),
    _c('nm-app', 'Court of Appeals', 'N.M. Ct. App.', IN)] },
  // NEW YORK trap: the Court of Appeals is the HIGHEST court; the trial court is confusingly named "Supreme Court".
  { id: 'ny', name: 'New York', abbr: 'N.Y.', courts: [
    _c('ny-coa', 'Court of Appeals', 'N.Y.', LR),
    _c('ny-ad', 'Supreme Court, Appellate Division', 'N.Y. App. Div.', IN),
    _c('ny-sup', 'Supreme Court (trial court)', 'N.Y. Sup. Ct.', TR)] },
  { id: 'nc', name: 'North Carolina', abbr: 'N.C.', courts: [
    _c('nc-sc', 'Supreme Court', 'N.C.', LR),
    _c('nc-app', 'Court of Appeals', 'N.C. Ct. App.', IN)] },
  { id: 'nd', name: 'North Dakota', abbr: 'N.D.', courts: [
    _c('nd-sc', 'Supreme Court', 'N.D.', LR),
    _c('nd-app', 'Court of Appeals', 'N.D. Ct. App.', IN)] },
  { id: 'oh', name: 'Ohio', abbr: 'Ohio', courts: [
    _c('oh-sc', 'Supreme Court', 'Ohio', LR),
    _c('oh-app', 'Court of Appeals', 'Ohio Ct. App.', IN),
    _c('oh-cp', 'Court of Common Pleas', 'Ohio C.P.', TR)] },
  // OKLAHOMA trap: TWO courts of last resort — Supreme Court (civil) + Court of Criminal Appeals (criminal).
  { id: 'ok', name: 'Oklahoma', abbr: 'Okla.', courts: [
    _c('ok-sc', 'Supreme Court', 'Okla.', LR),
    _c('ok-crim', 'Court of Criminal Appeals', 'Okla. Crim. App.', LR),
    _c('ok-civ', 'Court of Civil Appeals', 'Okla. Civ. App.', IN)] },
  { id: 'or', name: 'Oregon', abbr: 'Or.', courts: [
    _c('or-sc', 'Supreme Court', 'Or.', LR),
    _c('or-app', 'Court of Appeals', 'Or. Ct. App.', IN),
    _c('or-tax', 'Tax Court', 'Or. T.C.', TR)] },
  { id: 'pa', name: 'Pennsylvania', abbr: 'Pa.', courts: [
    _c('pa-sc', 'Supreme Court', 'Pa.', LR),
    _c('pa-super', 'Superior Court', 'Pa. Super. Ct.', IN),
    _c('pa-commw', 'Commonwealth Court', 'Pa. Commw. Ct.', IN)] },
  { id: 'ri', name: 'Rhode Island', abbr: 'R.I.', courts: [
    _c('ri-sc', 'Supreme Court', 'R.I.', LR)] },
  { id: 'sc', name: 'South Carolina', abbr: 'S.C.', courts: [
    _c('sc-sc', 'Supreme Court', 'S.C.', LR),
    _c('sc-app', 'Court of Appeals', 'S.C. Ct. App.', IN)] },
  { id: 'sd', name: 'South Dakota', abbr: 'S.D.', courts: [
    _c('sd-sc', 'Supreme Court', 'S.D.', LR)] },
  // TENNESSEE: two intermediate courts (civil Court of Appeals + Court of Criminal Appeals).
  { id: 'tn', name: 'Tennessee', abbr: 'Tenn.', courts: [
    _c('tn-sc', 'Supreme Court', 'Tenn.', LR),
    _c('tn-app', 'Court of Appeals', 'Tenn. Ct. App.', IN),
    _c('tn-crim', 'Court of Criminal Appeals', 'Tenn. Crim. App.', IN)] },
  // TEXAS trap: TWO courts of last resort — Supreme Court (civil) + Court of Criminal Appeals (criminal).
  { id: 'tx', name: 'Texas', abbr: 'Tex.', courts: [
    _c('tx-sc', 'Supreme Court', 'Tex.', LR),
    _c('tx-crim', 'Court of Criminal Appeals', 'Tex. Crim. App.', LR),
    _c('tx-app', 'Courts of Appeals', 'Tex. App.', IN),
    _c('tx-dist', 'District Courts', 'Tex. Dist. Ct.', TR)] },
  { id: 'ut', name: 'Utah', abbr: 'Utah', courts: [
    _c('ut-sc', 'Supreme Court', 'Utah', LR),
    _c('ut-app', 'Court of Appeals', 'Utah Ct. App.', IN)] },
  { id: 'vt', name: 'Vermont', abbr: 'Vt.', courts: [
    _c('vt-sc', 'Supreme Court', 'Vt.', LR)] },
  { id: 'va', name: 'Virginia', abbr: 'Va.', courts: [
    _c('va-sc', 'Supreme Court', 'Va.', LR),
    _c('va-app', 'Court of Appeals', 'Va. Ct. App.', IN),
    _c('va-cir', 'Circuit Court', 'Va. Cir. Ct.', TR)] },
  { id: 'wa', name: 'Washington', abbr: 'Wash.', courts: [
    _c('wa-sc', 'Supreme Court', 'Wash.', LR),
    _c('wa-app', 'Court of Appeals', 'Wash. Ct. App.', IN)] },
  { id: 'wv', name: 'West Virginia', abbr: 'W. Va.', courts: [
    _c('wv-sc', 'Supreme Court of Appeals', 'W. Va.', LR),
    _c('wv-ica', 'Intermediate Court of Appeals', 'W. Va. Ct. App.', IN, 'Intermediate court added 2022 (post-book-edition).')] },
  { id: 'wi', name: 'Wisconsin', abbr: 'Wis.', courts: [
    _c('wi-sc', 'Supreme Court', 'Wis.', LR),
    _c('wi-app', 'Court of Appeals', 'Wis. Ct. App.', IN)] },
  { id: 'wy', name: 'Wyoming', abbr: 'Wyo.', courts: [
    _c('wy-sc', 'Supreme Court', 'Wyo.', LR)] },
]);

// ─── TERRITORIES (Indigo Book T3) ───────────────────────────────────────────
export const TERRITORIES = Object.freeze([
  { id: 'pr', name: 'Puerto Rico', abbr: 'P.R.', courts: [
    _c('pr-sc', 'Supreme Court', 'P.R.', LR),
    _c('pr-app', 'Court of Appeals', 'P.R. Cir.', IN)] },
  { id: 'gu', name: 'Guam', abbr: 'Guam', courts: [
    _c('gu-sc', 'Supreme Court of Guam', 'Guam', LR)] },
  { id: 'vi', name: 'U.S. Virgin Islands', abbr: 'V.I.', courts: [
    // VI Superior Court removed from the picker — no corpus presence (0 cases_v2 rows; absent from cl_courts).
    _c('vi-sc', 'Supreme Court of the Virgin Islands', 'V.I.', LR, 'Book gives only "All courts: Cite to V.I."; VI Supreme Court est. 2007.')] },
  { id: 'mp', name: 'Northern Mariana Islands', abbr: 'N. Mar. I.', courts: [
    _c('mp-sc', 'Supreme Court', 'N. Mar. I.', LR)] },
  { id: 'as', name: 'American Samoa', abbr: 'Am. Sam.', courts: [ // 'Am. Sam.' per Indigo Book Table T12
    _c('as-hc', 'High Court of American Samoa', 'Am. Samoa', LR)] },
]);

// ─── Convenience accessors (used by the picker + A7) ─────────────────────────
/** Flat list of every jurisdiction group (federal is one group; each state/territory is a group). */
export function allJurisdictions() {
  return [
    { id: FEDERAL.id, name: FEDERAL.label, kind: 'federal' },
    ...STATES.map((s) => ({ id: s.id, name: s.name, abbr: s.abbr, kind: 'state' })),
    ...TERRITORIES.map((t) => ({ id: t.id, name: t.name, abbr: t.abbr, kind: 'territory' })),
  ];
}

/** Count summary for auditing against the book. */
export const COUNTS = Object.freeze({
  states: STATES.length,                 // 50 + D.C. = 51
  territories: TERRITORIES.length,       // 5
  circuits: FEDERAL.circuits.length,     // 13
  districts: FEDERAL.circuits.reduce((n, c) => n + c.districts.length, 0),
  specialty: FEDERAL.specialty.length,
});

export default { LEVEL, FEDERAL, STATES, TERRITORIES, allJurisdictions, COUNTS };
